import { useState } from "react";
import axios from "axios";
import { COMPANY, IMG } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMsg("");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setErrorMsg(
        Array.isArray(detail)
          ? detail.map((d) => d.msg).join(" · ")
          : detail || "Something went wrong. Please try again."
      );
      setStatus("error");
    }
  };

  return (
    <main data-testid="contact-page" style={{ background: "var(--cream)" }}>
      {/* HERO */}
      <section className="pt-40 pb-20 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage: `url(${IMG.mistyMountains})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        ></div>
        <div className="container-cu relative z-10 max-w-3xl">
          <p className="eyebrow mb-6">Get in Touch</p>
          <h1 className="heading-display text-5xl sm:text-6xl lg:text-7xl mb-6">
            Let's <span className="heading-italic">talk</span>.
          </h1>
          <p className="text-lg leading-relaxed" style={{ color: "var(--brown-soft)" }}>
            For stays, partnerships, press, or anything else — we read every
            message ourselves.
          </p>
        </div>
      </section>

      {/* CONTACT GRID */}
      <section className="section pt-0">
        <div className="container-cu grid lg:grid-cols-12 gap-10">
          {/* INFO */}
          <div className="lg:col-span-4 space-y-6">
            <div
              className="p-7 rounded-md"
              style={{ background: "var(--forest)", color: "var(--cream)" }}
            >
              <p className="eyebrow eyebrow-light mb-4" style={{ color: "var(--gold-soft)" }}>
                Email
              </p>
              <a
                href={`mailto:${COMPANY.email}`}
                className="font-serif text-2xl link-underline"
                data-testid="contact-email-link"
              >
                {COMPANY.email}
              </a>
              <p className="mt-4 text-sm opacity-80">
                Replied to personally, usually within 48 hours.
              </p>
            </div>

            <div className="p-7 rounded-md" style={{ background: "var(--white)", border: "1px solid var(--line)" }}>
              <p className="eyebrow mb-4">Registered Office</p>
              <p className="font-serif text-lg" style={{ color: "var(--forest)" }}>
                {COMPANY.name}
              </p>
              <p className="text-sm mt-2 leading-relaxed" style={{ color: "var(--brown-soft)" }}>
                {COMPANY.address}
              </p>
              <div className="grid grid-cols-2 gap-4 mt-6 pt-6" style={{ borderTop: "1px solid var(--line)" }}>
                <div>
                  <p className="eyebrow mb-2">KvK</p>
                  <p className="text-sm" style={{ color: "var(--brown)" }}>{COMPANY.kvk}</p>
                </div>
                <div>
                  <p className="eyebrow mb-2">BTW</p>
                  <p className="text-sm" style={{ color: "var(--brown)" }}>{COMPANY.btw}</p>
                </div>
              </div>
            </div>

            <div
              className="p-7 rounded-md"
              style={{ background: "var(--gold)", color: "var(--brown)" }}
            >
              <p className="eyebrow mb-3" style={{ color: "var(--brown)" }}>
                Investors & Partners
              </p>
              <p className="font-serif text-xl mb-2">
                Seeking €1M – €1.5M
              </p>
              <p className="text-sm">
                Profit-sharing model. No equity transfer. Reach out directly to{" "}
                <a href={`mailto:${COMPANY.email}`} className="link-underline font-medium">
                  {COMPANY.email}
                </a>
              </p>
            </div>
          </div>

          {/* FORM */}
          <div className="lg:col-span-8">
            <div
              className="p-8 sm:p-12 rounded-md"
              style={{ background: "var(--white)", border: "1px solid var(--line)" }}
            >
              {status === "success" ? (
                <div className="text-center py-14" data-testid="contact-success">
                  <div
                    className="w-14 h-14 rounded-full inline-flex items-center justify-center mb-5"
                    style={{ background: "var(--gold)", color: "var(--forest)" }}
                  >
                    <i className="fa-solid fa-check text-xl"></i>
                  </div>
                  <h3 className="heading-display text-3xl mb-3">Thank you — message received.</h3>
                  <p className="text-sm max-w-md mx-auto" style={{ color: "var(--brown-soft)" }}>
                    We'll get back to you personally at the email address you provided.
                  </p>
                  <button
                    onClick={() => setStatus("idle")}
                    className="mt-7 text-xs uppercase tracking-[0.2em] link-underline"
                    style={{ color: "var(--gold-deep)" }}
                    data-testid="contact-reset"
                  >
                    Send another message →
                  </button>
                </div>
              ) : (
                <>
                  <p className="eyebrow mb-3">Write to us</p>
                  <h2 className="heading-display text-3xl sm:text-4xl mb-8">
                    A note <span className="heading-italic">— always read</span>.
                  </h2>
                  <form onSubmit={submit} className="grid gap-5" data-testid="contact-form" noValidate>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="label-field">Your name</label>
                        <input
                          data-testid="contact-name"
                          type="text"
                          name="name"
                          required
                          value={form.name}
                          onChange={onChange}
                          className="input-field"
                          placeholder="First & last name"
                        />
                      </div>
                      <div>
                        <label className="label-field">Email</label>
                        <input
                          data-testid="contact-email"
                          type="email"
                          name="email"
                          required
                          value={form.email}
                          onChange={onChange}
                          className="input-field"
                          placeholder="you@email.com"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="label-field">Subject (optional)</label>
                      <input
                        data-testid="contact-subject"
                        type="text"
                        name="subject"
                        value={form.subject}
                        onChange={onChange}
                        className="input-field"
                        placeholder="Stay, partnership, press, other..."
                      />
                    </div>
                    <div>
                      <label className="label-field">Message</label>
                      <textarea
                        data-testid="contact-message"
                        name="message"
                        rows={6}
                        required
                        value={form.message}
                        onChange={onChange}
                        className="input-field resize-none"
                        placeholder="Tell us what's on your mind..."
                      />
                    </div>
                    {status === "error" && (
                      <p className="text-sm" data-testid="contact-error" style={{ color: "#a04040" }}>
                        {errorMsg}
                      </p>
                    )}
                    <button
                      data-testid="contact-submit"
                      type="submit"
                      disabled={status === "loading"}
                      className="btn-primary justify-center mt-2 disabled:opacity-60"
                    >
                      {status === "loading" ? (
                        <>
                          <i className="fa-solid fa-circle-notch fa-spin"></i> Sending...
                        </>
                      ) : (
                        <>
                          Send Message <i className="fa-solid fa-arrow-right text-sm"></i>
                        </>
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
